import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CreateTestTemplateMutation,
  TestTemplateCreateFailure,
  TestTemplateCreateInput,
  TestTemplateMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateTestTemplateDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateTestTemplateReturn {
  create: (input: TestTemplateCreateInput) => Promise<void>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateTestTemplate = (onSuccess: () => void): UseCreateTestTemplateReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    CreateTestTemplateMutation,
    TestTemplateMutationCreateArgs
  >(CreateTestTemplateDocument)

  const create = async (input: TestTemplateCreateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.testTemplate.create
    const hasError = error || output?.__typename === "TestTemplateCreateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message:
          error?.message ?? (output as TestTemplateCreateFailure).error.message ?? "Failed to create test template.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Test template successfully created.",
    })
    onSuccess()
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
